import { FormikProps } from "formik";
import BaseQuestionIcon from "../../../../icons/base-question.icon";
import DropdownSelector from "../../../../Common/dropdown-selector";
import PlusIcon from "../../../../icons/plus.icon";
import CrossIcon from "../../../../icons/cross.icon";
import cn from "../../../../../Utils/class-names.utils";
import styles from "./index.module.scss";
import HighgroundDatePicker from "../../../../test/TestComponent";
import MyTooltip from "../../../../Common/my-tooltip/my-tooltip.component";
import ReviewMoreToolsTooltip from "../../../../Common/review-more-tools";
import { IProductFormValues } from "../../../../../common/validations/product.validations";
import { BILLING_PERIODS, TIME_UNITS } from "../initial-values";
import FormRow from "../form-row";

interface PeriodDetailsProps extends FormikProps<IProductFormValues> {}

const PeriodDetails = ({
  values,
  setFieldValue,
}: PeriodDetailsProps) => {
  const licenseQty = values.licenseQty;
  const selectedPeriod = values.billingPeriod;
  const selectedSnapToUnit = values.snapToUnit;
  const selectedMonitoredBySoc = values.monitoredBySoc;
  const selectedTimeUnit = values.contractTermUnit;
  const contractTerm = values.contractTerm;
  const selectedManufacturers = values.periodManufacturers;
  const contractEnd = values.contractEnd;

  const handleRemoveManufacturer = (id: string) => {
    setFieldValue(
      'periodManufacturers',
      selectedManufacturers.filter(m => m.id !== id),
    );
  };

  const handleAddManufacturer = (item: { label: string; value: string }) => {
    setFieldValue('periodManufacturers', [
      ...selectedManufacturers,
      { id: item.value, name: item.label },
    ]);
  };

  const getAvailableManufacturers = () => {
    return [];
  };

  const billingPeriods = BILLING_PERIODS;
  const timeUnits = TIME_UNITS;

  const handleSelectPeriod = (item: { label: string; value: string }) => {
    setFieldValue('billingPeriod', item.value);
  };

  const handleSelectSnapToUnit = (item: { label: string; value: string }) => {
    setFieldValue('snapToUnit', item.value);
  };

  const handleSelectMonitoredBySoc = (item: { label: string; value: string }) => {
    setFieldValue('monitoredBySoc', item.value);
  };

  const handleSelectTimeUnit = (item: { label: string; value: string }) => {
    setFieldValue('contractTermUnit', item.value);
  };

  const selectedBillingPeriod = billingPeriods.find(p => p.value === selectedPeriod) || null;
  const selectedSnapToUnitItem = billingPeriods.find(p => p.value === selectedSnapToUnit) || null;
  const selectedMonitoredBySocItem = billingPeriods.find(p => p.value === selectedMonitoredBySoc) || null;
  const selectedTimeUnitItem = timeUnits.find(u => u.value === selectedTimeUnit) || null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('licenseQty', e.target.value);
  };

  return (
    <div className={styles.container}>
      <FormRow label="License Qty" htmlFor="license-qty-input" styles={styles}>
        <div className={styles.inputWrapper}>
          <div className={styles.inputInner}>
            <input
              id="license-qty-input"
              type="text"
              value={licenseQty}
              onChange={handleInputChange}
              className={styles.input}
              aria-label="License Qty"
            />
          </div>
        </div>
      </FormRow>

      <FormRow
        label="Billing period"
        htmlFor="billing-period-select"
        variant="withPadding"
        labelIcon={<BaseQuestionIcon className="fill-current f-grey" />}
        styles={styles}
      >
        <div className={styles.relative}>
          <DropdownSelector
            list={billingPeriods}
            selected={selectedBillingPeriod}
            onItemSelected={handleSelectPeriod}
            className={styles.dropdownWrapper}
          >
            <div className={styles.dropdownButton}>
              <div className={styles.dropdownButtonInner}>
                <span className={styles.dropdownButtonText}>
                  {selectedPeriod}
                </span>
              </div>

              <div className={styles.arrowIconWrapper}>
                <div className="arrow-icon" />
              </div>
            </div>
          </DropdownSelector>
        </div>
      </FormRow>

      <FormRow
        label="Snap to unit"
        htmlFor="snap-to-unit-select"
        variant="withPadding"
        labelIcon={<BaseQuestionIcon className="fill-current f-grey" />}
        styles={styles}
      >
        <div className={styles.relative}>
          <DropdownSelector
            list={billingPeriods}
            selected={selectedSnapToUnitItem}
            onItemSelected={handleSelectSnapToUnit}
            className={styles.dropdownWrapper}
          >
            <div className={styles.dropdownButton}>
              <div className={styles.dropdownButtonInner}>
                <span className={styles.dropdownButtonText}>
                  {selectedSnapToUnit}
                </span>
              </div>

              <div className={styles.arrowIconWrapper}>
                <div className="arrow-icon" />
              </div>
            </div>
          </DropdownSelector>
        </div>
      </FormRow>

      <FormRow label="Contract Term" htmlFor="contract-term-input" styles={styles}>
        <div className={styles.inputWrapper}>
          <div className={styles.inputInner}>
            <input
              id="contract-term-input"
              type="text"
              value={contractTerm}
              onChange={(e) => setFieldValue('contractTerm', e.target.value)}
              className={styles.input}
              aria-label="Contract Term Input"
            />
          </div>
        </div>

        <DropdownSelector
          list={timeUnits}
          selected={selectedTimeUnitItem}
          onItemSelected={handleSelectTimeUnit}
          className={styles.dropdownWrapper}
        >
          <div className={styles.dropdownButton}>
            <div className={styles.dropdownButtonInner}>
              <span className={styles.dropdownButtonText}>
                {selectedTimeUnit}
              </span>
            </div>

            <div className={styles.arrowIconWrapper}>
              <div className="arrow-icon" />
            </div>
          </div>
        </DropdownSelector>
      </FormRow>

      <FormRow label="Contract End" htmlFor="contract-end-input" styles={styles}>
        <div className={styles.contractEndContainer}>
          <div className={styles.contractEndInputWrapper}>
            <HighgroundDatePicker
              selected={contractEnd}
              onChange={(date: any) => setFieldValue('contractEnd', date)}
              className={styles.contractEndInput}
              aria-label="Contract End Date"
              id="contract-end-input"
              popperConfig={{ strategy: "fixed" }}
            />
          </div>
        </div>
      </FormRow>

      <FormRow
        label="Monitored by SOC"
        htmlFor="monitored-by-soc-select"
        variant="withPadding"
        styles={styles}
      >
        <div className={styles.relative}>
          <DropdownSelector
            list={billingPeriods}
            selected={selectedMonitoredBySocItem}
            onItemSelected={handleSelectMonitoredBySoc}
            className={styles.dropdownWrapper}
          >
            <div className={styles.dropdownButton}>
              <div className={styles.dropdownButtonInner}>
                <span className={styles.dropdownButtonText}>
                  {selectedMonitoredBySoc}
                </span>
              </div>

              <div className={styles.arrowIconWrapper}>
                <div className="arrow-icon" />
              </div>
            </div>
          </DropdownSelector>
        </div>
      </FormRow>

      <FormRow label="Manufacturer" variant="supplier" styles={styles}>
        <div className={styles.suppliersContainer}>
          {selectedManufacturers.map((manufacturer) => (
            <div
              key={manufacturer.id}
              className={styles.supplierChip}
            >
              <div className={styles.supplierIconWrapper}>
                {/* <BaseCircleM className="!relative !w-5 !h-5" /> */}
              </div>

              <div className={styles.supplierNameWrapper}>
                <span className={styles.supplierName}>
                  {manufacturer.name}
                </span>
              </div>

              <div className={styles.supplierRemoveButtonWrapper}>
                <button
                  type="button"
                  onClick={() => handleRemoveManufacturer(manufacturer.id)}
                  className={cn(styles.removeButton, "my-button")}
                  aria-label={`Remove ${manufacturer.name}`}
                >
                  <div className={styles.removeButtonInner}>
                    <CrossIcon />
                  </div>
                </button>
              </div>
            </div>
          ))}
          <MyTooltip
            renderTooltip={(props: any) => (
              <ReviewMoreToolsTooltip
                {...props}
                className={styles.reviewMoreToolsTooltip}
              />
            )}
            disabled={false}
            hideOnMouseLeave={false}
            delay={300}
            onClose={() => { }}
            isFixed={true}
          >
            <div className="d-flex align-items-center">
              <div className={styles.addSupplierIconWrapper}>
                <PlusIcon />
              </div>

              <div className={styles.addSupplierTextWrapper}>
                <span className={styles.addSupplierText}>
                  Add
                </span>
              </div>
            </div>
          </MyTooltip>
        </div>
      </FormRow>
    </div>
  );
};

export default PeriodDetails;